/**
 * useSuspenseSetData Hook - React Suspense対応の個別セットデータ取得
 */

import { useSuspenseQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queryKeys } from '../../../lib/queryKeys'
import { setQueryOptions } from '../../../lib/queryClient'
import { useStore } from '../../../store'
import type { ImageSet, SetItem, Image } from '../../../core/types'

export interface SetDataResult {
  set: ImageSet
  items: SetItem[]
  images: Record<string, Image>
  imageUrls: string[]
}

/**
 * React Suspense対応の個別セットデータを取得するフック
 * デッキID指定で、そのデッキの全データ（セット、アイテム、画像）を取得
 * @param setId - 取得するセットのID
 */
export function useSuspenseSetData(setId: string | undefined) {
  const appState = useStore(state => state.appState)
  
  // useSuspenseQueryを使用 - データ取得中は自動的にSuspenseがトリガーされる
  const { data } = useSuspenseQuery({
    queryKey: queryKeys.sets.detail(setId || 'none'),
    queryFn: async (): Promise<SetDataResult | null> => {
      if (!setId) return null
      
      // セット情報を取得
      const set = appState.imageSets[setId]
      if (!set) return null
      
      // セットに含まれるアイテムを取得
      const items = Object.values(appState.setItems)
        .filter(item => item.setId === setId)
        .sort((a, b) => a.order - b.order)
      
      // アイテムに対応する画像を取得
      const imageIds = items.map(item => item.imageId)
      const images: Record<string, Image> = {}
      const imageUrls: string[] = []
      
      for (const imageId of imageIds) {
        const image = appState.images[imageId]
        if (image && image.url) {
          images[imageId] = image
          imageUrls.push(image.url)
        }
      }
      
      return {
        set,
        items,
        images,
        imageUrls
      }
    },
    ...setQueryOptions,
  })
  
  // データの後処理（必要に応じて）
  const processedData = useMemo(() => {
    if (!data) {
      return {
        set: null,
        items: [],
        images: {},
        imageUrls: [],
        currentIndex: 0,
      }
    }
    
    return {
      ...data,
      currentIndex: 0, // 初期インデックス
    }
  }, [data])
  
  return processedData
}